import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useAtlasStore, AtlasState } from '../../../store/atlasStore';
import { useIframeNavigation } from '../../../hooks/useIframeNavigation';
import styles from '../styles/InboxView.module.css';
import { SparkProjectOverview } from '../types/sparkTypes';
import { FlowSelectionModal } from '../components/modals/FlowSelectionModal';
import { ConfirmationModal } from '../components/modals/ConfirmationModal';
import { MyLibraryItemsTab } from '../components/search/MyLibraryItemsTab';
import { CreativeEditor } from '../components/modals/CreativeEditor';

const InboxEmptyIcon = () => (
  <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 18h6M10 22h4M12 2v1M4.22 7.22l.71.71M2 13h1M21 13h1M19.07 7.93l.71-.71" />
    <path d="M18 13a6 6 0 0 0-12 0c0 2.22 1.21 4.16 3 5.19V20a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1v-1.81c1.79-1.03 3-2.97 3-5.19z" />
  </svg>
);

// Kebab Menu Icon (Placeholder SVG)
const KebabIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 8C13.1 8 14 7.1 14 6C14 4.9 13.1 4 12 4C10.9 4 10 4.9 10 6C10 7.1 10.9 8 12 8ZM12 10C10.9 10 10 10.9 10 12C10 13.1 10.9 14 12 14C13.1 14 14 13.1 14 12C14 10.9 13.1 10 12 10ZM12 16C10.9 16 10 16.9 10 18C10 19.1 10.9 20 12 20C13.1 20 14 19.1 14 18C14 16.9 13.1 16 12 16Z" fill="currentColor" />
  </svg>
);

interface ProjectTableProps {
  projects: SparkProjectOverview[];
  onOpen: (fqn: string) => void;
  onDelete: (project: SparkProjectOverview) => void;
  onClose: (project: SparkProjectOverview) => void;
  onReopen: (project: SparkProjectOverview) => void;
}

const ProjectTable: React.FC<ProjectTableProps> = ({ projects, onOpen, onDelete, onClose, onReopen }) => {
  const [openMenuFqn, setOpenMenuFqn] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const toggleMenu = (fqn: string) => {
    setOpenMenuFqn(prev => (prev === fqn ? null : fqn));
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenMenuFqn(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  if (!projects || projects.length === 0) {
    return <p className={styles.noItemsMessage}>No Innovation Spaces found.</p>;
  }

  return (
    <table className={styles.projectTable}>
      <thead>
        <tr>
          <th className={styles.actionHeader}></th>
          <th>Project</th>
          <th>Type</th>
          <th>Created On</th>
          <th className={styles.statusColumn}>Status</th>
        </tr>
      </thead>
      <tbody>
        {projects.map((project) => (
          <tr key={project.fqn} onClick={() => onOpen(project.fqn)} className={styles.clickableRow}>
            <td className={styles.actionCell}>
              <button
                className={styles.kebabButton}
                onClick={(e) => { e.stopPropagation(); toggleMenu(project.fqn); }}
              >
                <KebabIcon />
              </button>
              {openMenuFqn === project.fqn && (
                <div className={styles.actionMenu} ref={menuRef}>
                  <button onClick={(e) => { e.stopPropagation(); onOpen(project.fqn); setOpenMenuFqn(null); }}>Open</button>
                  {project.current_user_is_admin && !project.is_closed && (
                    <button onClick={(e) => { e.stopPropagation(); onClose(project); setOpenMenuFqn(null); }}>Close</button>
                  )}
                  {project.current_user_is_admin && project.is_closed && (
                    <button onClick={(e) => { e.stopPropagation(); onReopen(project); setOpenMenuFqn(null); }}>Re-open</button>
                  )}
                  {project.current_user_is_admin && (
                    <button onClick={(e) => { e.stopPropagation(); onDelete(project); setOpenMenuFqn(null); }} className={styles.deleteMenuItem}>Delete</button>
                  )}
                </div>
              )}
            </td>
            <td>{project.name || 'N/A'}</td>
            <td>{project._flow_name || 'N/A'}</td>
            <td>{project.created_at ? new Date(project.created_at).toLocaleDateString() : 'N/A'}</td>
            <td className={styles.statusColumn}>{project.is_closed ? 'Closed' : 'Active'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

// Main Inbox View Component
export const InboxView: React.FC = () => {
  const navigate = useIframeNavigation();

  // Select state pieces individually for stability
  const fetchSparkProjects = useAtlasStore((state: AtlasState) => state.fetchSparkProjects);
  const sparkProjects = useAtlasStore((state: AtlasState) => state.sparkProjects);
  const isLoadingProjects = useAtlasStore((state: AtlasState) => state.isLoadingSpark.projects);
  const sparkError = useAtlasStore((state: AtlasState) => state.sparkError);
  const deleteSparkProject = useAtlasStore((state: AtlasState) => state.deleteSparkProject);
  const createSparkProject = useAtlasStore((state: AtlasState) => state.createSparkProject);
  const closeSparkProject = useAtlasStore((state: AtlasState) => state.closeSparkProject);
  const reopenSparkProject = useAtlasStore((state: AtlasState) => state.reopenSparkProject);
  const sparkCreativeTypes = useAtlasStore((state: AtlasState) => state.sparkCreativeTypes);
  const fetchSparkCreativeTypes = useAtlasStore((state: AtlasState) => state.fetchSparkCreativeTypes);

  // Ensure creative types are loaded
  useEffect(() => {
    if (!sparkCreativeTypes || sparkCreativeTypes.length === 0) {
      fetchSparkCreativeTypes().catch(() => {/* non-fatal */});
    }
  }, [sparkCreativeTypes, fetchSparkCreativeTypes]);

  const libraryTypes = useMemo(
    () => (sparkCreativeTypes || []).filter(ct => ct.library).sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)),
    [sparkCreativeTypes],
  );

  // Dynamic tabs: projects + one per library type
  const tabs = useMemo(() => {
    const base = [{ key: 'projects', label: 'Innovation projects' }];
    return [
      ...base,
      ...libraryTypes.map(ct => ({
        key: ct.fqn,
        label: `My ${ct.plural_name || ct.name}`,
      })),
    ];
  }, [libraryTypes]);

  const [activeTab, setActiveTab] = useState('projects');

  // Tab indicator refs & state (ref map pattern from CreativeSearchView)
  const scopeTabsRef = useRef<HTMLDivElement>(null);
  const scopeTabRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const [scopeIndicatorStyle, setScopeIndicatorStyle] = useState({ left: 0, width: 0 });

  // Editor state — lifted from MyLibraryItemsTab so it renders at full view level
  const [editorRequest, setEditorRequest] = useState<{
    itemFqn: string;
    onClose: () => Promise<void>;
    onDataRefreshed: () => Promise<void>;
  } | null>(null);

  // State for modals
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<SparkProjectOverview | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCloseModalOpen, setIsCloseModalOpen] = useState(false);
  const [isReopenModalOpen, setIsReopenModalOpen] = useState(false);
  const [projectToToggle, setProjectToToggle] = useState<SparkProjectOverview | null>(null);
  const [isToggling, setIsToggling] = useState(false);

  // Fetch projects on mount
  useEffect(() => {
    fetchSparkProjects();
  }, [fetchSparkProjects]);

  // Tab indicator positioning — depends on activeTab
  useEffect(() => {
    const updateIndicator = () => {
      const container = scopeTabsRef.current;
      const activeEl = scopeTabRefs.current.get(activeTab);
      if (!container || !activeEl) return;
      const containerRect = container.getBoundingClientRect();
      const tabRect = activeEl.getBoundingClientRect();
      setScopeIndicatorStyle({
        left: tabRect.left - containerRect.left,
        width: tabRect.width,
      });
    };
    updateIndicator();
    window.addEventListener('resize', updateIndicator);
    return () => window.removeEventListener('resize', updateIndicator);
  }, [activeTab]);

  const handleOpenProject = (fqn: string) => {
    navigate(`/spark/project/${encodeURIComponent(fqn)}`);
  };

  const handleDeleteProject = (project: SparkProjectOverview) => {
    handleOpenDeleteModal(project);
  };

  const handleCloseProject = (project: SparkProjectOverview) => {
    setProjectToToggle(project);
    setIsCloseModalOpen(true);
  };

  const handleReopenProject = (project: SparkProjectOverview) => {
    setProjectToToggle(project);
    setIsReopenModalOpen(true);
  };

  const confirmCloseProject = async () => {
    if (!projectToToggle) return;
    setIsToggling(true);
    try {
      await closeSparkProject(projectToToggle.fqn);
      await fetchSparkProjects();
    } finally {
      setIsToggling(false);
      setIsCloseModalOpen(false);
      setProjectToToggle(null);
    }
  };

  const confirmReopenProject = async () => {
    if (!projectToToggle) return;
    setIsToggling(true);
    try {
      await reopenSparkProject(projectToToggle.fqn);
      await fetchSparkProjects();
    } finally {
      setIsToggling(false);
      setIsReopenModalOpen(false);
      setProjectToToggle(null);
    }
  };

  const handleOpenDeleteModal = (project: SparkProjectOverview) => {
    setProjectToDelete(project);
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setProjectToDelete(null);
  };

  const handleConfirmDelete = () => {
    if (!projectToDelete) return;

    setIsDeleting(true);
    deleteSparkProject(projectToDelete.fqn)
      .then((success: boolean) => {
        if (success) {
          console.log("Delete successful, refreshing...");
          fetchSparkProjects();
        } else {
          console.error(`Delete failed for ${projectToDelete.fqn}`);
        }
      })
      .catch((err: any) => {
        console.error(`Error during delete room call for ${projectToDelete.fqn}:`, err);
      })
      .finally(() => {
        setIsDeleting(false);
        handleCloseDeleteModal();
      });
  };

  const handleOpenCreateModal = () => setIsCreateModalOpen(true);
  const handleCloseCreateModal = () => setIsCreateModalOpen(false);

  const handleProjectCreate = async (flowFqn: string, projectName: string): Promise<string | null> => {
    const newProjectFqn = await createSparkProject(flowFqn, projectName);
    if (newProjectFqn) {
      navigate(`/spark/project/${encodeURIComponent(newProjectFqn)}`);
      return newProjectFqn;
    } else {
      return null;
    }
  };

  // Find the library type metadata for the active tab (if it's a library tab)
  const activeLibraryType = activeTab !== 'projects'
    ? libraryTypes.find(ct => ct.fqn === activeTab) || null
    : null;

  // When an editor is open, render it as the full standalone view
  if (editorRequest) {
    return (
      <CreativeEditor
        isOpen={true}
        onClose={async () => {
          await editorRequest.onClose();
          setEditorRequest(null);
        }}
        editorMode={'item'}
        itemFqn={editorRequest.itemFqn}
        onDataRefreshed={editorRequest.onDataRefreshed}
      />
    );
  }

  return (
    <div className={styles.inboxViewContainer}>
      <div className={styles.mainContentWrapper}>
        <header className={styles.header}>
          <h1>Innovation Studio</h1>
        </header>

        <div className={styles.scopeTabs} ref={scopeTabsRef}>
          {tabs.map(tab => (
            <button
              key={tab.key}
              type="button"
              className={`${styles.scopeTab} ${activeTab === tab.key ? styles.scopeTabActive : ''}`}
              onClick={() => setActiveTab(tab.key)}
              ref={(el) => {
                if (el) scopeTabRefs.current.set(tab.key, el);
                else scopeTabRefs.current.delete(tab.key);
              }}
            >
              {tab.label}
            </button>
          ))}
          <div
            className={styles.scopeTabIndicator}
            style={{ left: scopeIndicatorStyle.left, width: scopeIndicatorStyle.width }}
          />
        </div>

        {activeTab === 'projects' ? (
          <div className={styles.content}>
            {isLoadingProjects && <p>Loading projects...</p>}
            {sparkError && <p className={styles.error}>Error loading projects: {sparkError}</p>}
            {!isLoadingProjects && !sparkError && sparkProjects.length > 0 && (
              <div className={styles.createButtonRow}>
                <button onClick={handleOpenCreateModal} className={styles.createButton}>+ Create project</button>
              </div>
            )}
            {!isLoadingProjects && !sparkError && sparkProjects.length === 0 && (
              <div className={styles.emptyState}>
                <div className={styles.emptyStateIcon}>
                  <InboxEmptyIcon />
                </div>
                <h3 className={styles.emptyStateTitle}>No projects yet</h3>
                <p className={styles.emptyStateDescription}>
                  Create your first innovation project to start exploring opportunities
                  and generating ideas.
                </p>
                <button
                  type="button"
                  className={styles.emptyStateButton}
                  onClick={handleOpenCreateModal}
                >
                  + Create project
                </button>
              </div>
            )}
            {!isLoadingProjects && !sparkError && sparkProjects.length > 0 && (
              <div className={styles.tablePanel}>
                <ProjectTable
                  projects={sparkProjects}
                  onOpen={handleOpenProject}
                  onDelete={handleDeleteProject}
                  onClose={handleCloseProject}
                  onReopen={handleReopenProject}
                />
              </div>
            )}
          </div>
        ) : activeLibraryType ? (
          <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', flex: 1, minHeight: 0 }}>
            <MyLibraryItemsTab
              key={activeTab}
              creativeTypeFqn={activeLibraryType.fqn}
              creativeTypeName={activeLibraryType.name}
              creativeTypePluralName={activeLibraryType.plural_name || activeLibraryType.name}
              onEditorChange={setEditorRequest}
            />
          </div>
        ) : null}
      </div>

      <FlowSelectionModal
        isOpen={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        onCreate={handleProjectCreate}
      />

      {projectToDelete && (
        <ConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={handleCloseDeleteModal}
          onConfirm={handleConfirmDelete}
          title="Confirm Project Deletion"
          message={`Are you sure you want to delete "${projectToDelete.name || projectToDelete.fqn}"? This action cannot be undone.`}
          confirmText="Delete"
          isConfirming={isDeleting}
          variant="destructive"
        />
      )}

      {projectToToggle && (
        <>
          <ConfirmationModal
            isOpen={isCloseModalOpen}
            onClose={() => { setIsCloseModalOpen(false); setProjectToToggle(null); }}
            onConfirm={confirmCloseProject}
            title="Close Project?"
            message={`Closing "${projectToToggle.name || projectToToggle.fqn}" will make it read-only. You can re-open it later.`}
            confirmText="Close"
            isConfirming={isToggling}
            variant="destructive"
          />
          <ConfirmationModal
            isOpen={isReopenModalOpen}
            onClose={() => { setIsReopenModalOpen(false); setProjectToToggle(null); }}
            onConfirm={confirmReopenProject}
            title="Re-open Project?"
            message={`Re-opening "${projectToToggle.name || projectToToggle.fqn}" will allow edits and new content.`}
            confirmText="Re-open"
            isConfirming={isToggling}
            variant="primary"
          />
        </>
      )}
    </div>
  );
};
